import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ExternalLink, Copy, Check, Search, Filter, Globe,
  Home, Package, Users, ShoppingCart, Settings, BarChart3,
  User, MessageSquare, FileText, DollarSign, Truck, Star,
  Shield, Building, Store, UserPlus, HelpCircle, AlertTriangle,
  Grid
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface AdminUrl {
  id: string;
  name: string;
  path: string;
  category: string;
  description: string | null;
  icon: string | null;
}

const AllUrlsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [copiedUrl, setCopiedUrl] = useState('');
  const [urls, setUrls] = useState<AdminUrl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch URLs from database
  useEffect(() => {
    const fetchUrls = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('admin_urls')
          .select('*')
          .order('category')
          .order('name');
        
        if (error) throw error;
        
        // Add hardcoded URLs if they don't exist in the database
        const hardcodedUrls = getHardcodedUrls();
        
        // Combine database URLs with hardcoded URLs, avoiding duplicates
        const existingPaths = new Set(data?.map(url => url.path) || []);
        const additionalUrls = hardcodedUrls.filter(url => !existingPaths.has(url.path));
        
        const combinedUrls = [...(data || []), ...additionalUrls];
        setUrls(combinedUrls);
      } catch (err) {
        console.error('Error fetching URLs:', err);
        setError('Failed to load URLs');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUrls();
  }, []);

  // Hardcoded URLs to ensure all routes are displayed
  const getHardcodedUrls = (): AdminUrl[] => {
    return [
      // Public Pages
      { id: 'home', name: 'Home Page', path: '/', category: 'Public Pages', description: 'Main landing page with hero section and featured products', icon: 'Home' },
      { id: 'products', name: 'Products Page', path: '/products', category: 'Public Pages', description: 'Browse all products with filters and search', icon: 'Package' },
      { id: 'product-detail', name: 'Product Detail', path: '/product/1', category: 'Public Pages', description: 'Individual product page with details and seller info', icon: 'Package' },
      { id: 'suppliers', name: 'Suppliers Page', path: '/suppliers', category: 'Public Pages', description: 'Browse verified suppliers and manufacturers', icon: 'Users' },
      { id: 'supplier-store', name: 'Supplier Store', path: '/supplier/san-trade-limited', category: 'Public Pages', description: 'Individual supplier profile and products', icon: 'Store' },
      { id: 'seller-store', name: 'Seller Store', path: '/seller/bengal-textiles', category: 'Public Pages', description: 'Individual seller store page', icon: 'Building' },
      { id: 'about', name: 'About Page', path: '/about', category: 'Public Pages', description: 'Company information and team details', icon: 'Users' },
      { id: 'contact', name: 'Contact Page', path: '/contact', category: 'Public Pages', description: 'Contact form and company information', icon: 'MessageSquare' },
      { id: 'help', name: 'Help Center', path: '/help', category: 'Public Pages', description: 'Help and support information', icon: 'HelpCircle' },
      { id: 'services', name: 'Services', path: '/services', category: 'Public Pages', description: 'Services and support offerings', icon: 'Settings' },
      
      // Authentication Pages
      { id: 'signin', name: 'Sign In', path: '/signin', category: 'Authentication', description: 'User login page with email/password and QR code options', icon: 'User' },
      { id: 'create-account', name: 'Create Account', path: '/create-account', category: 'Authentication', description: 'User registration for buyers and suppliers', icon: 'UserPlus' },
      { id: 'become-exclusive', name: 'Become Exclusive', path: '/become-exclusive', category: 'Authentication', description: 'Premium membership signup page', icon: 'Star' },
      
      // Category Pages
      { id: 'garments', name: 'Garments & Apparel', path: '/category/garments-apparel', category: 'Product Categories', description: 'Clothing and textile products', icon: 'Package' },
      { id: 'jute', name: 'Jute & Natural Fiber', path: '/category/jute-natural-fiber', category: 'Product Categories', description: 'Eco-friendly jute products', icon: 'Package' },
      { id: 'frozen', name: 'Frozen Foods', path: '/category/frozen-foods', category: 'Product Categories', description: 'Frozen food products', icon: 'Package' },
      { id: 'leather', name: 'Leather Goods', path: '/category/leather-goods', category: 'Product Categories', description: 'Leather products and accessories', icon: 'Package' },
      { id: 'pharma', name: 'Pharmaceuticals & Health', path: '/category/pharmaceuticals-health', category: 'Product Categories', description: 'Medical and health products', icon: 'Package' },
      { id: 'scrap', name: 'Scrap & Recycling', path: '/category/scrap-recycling', category: 'Product Categories', description: 'Recycling and scrap materials', icon: 'Package' },
      
      // Admin Panel
      { id: 'admin-dashboard', name: 'Admin Dashboard', path: '/admin', category: 'Admin Panel', description: 'Main admin dashboard with analytics', icon: 'BarChart3' },
      { id: 'user-management', name: 'User Management', path: '/admin/users', category: 'Admin Panel', description: 'Manage buyers, suppliers, and user accounts', icon: 'Users' },
      { id: 'category-management', name: 'Category Management', path: '/admin/categories', category: 'Admin Panel', description: 'Manage product categories, subcategories, and hierarchy', icon: 'Grid' },
      { id: 'product-management', name: 'Product Management', path: '/admin/products', category: 'Admin Panel', description: 'Manage products, inventory, and listings', icon: 'Package' },
      { id: 'order-management', name: 'Order Management', path: '/admin/orders', category: 'Admin Panel', description: 'Track and manage customer orders', icon: 'ShoppingCart' },
      { id: 'supplier-management', name: 'Supplier Management', path: '/admin/suppliers', category: 'Admin Panel', description: 'Manage and verify supplier accounts', icon: 'Shield' },
      { id: 'payment-management', name: 'Payment Management', path: '/admin/payments', category: 'Admin Panel', description: 'Track payments, refunds, and transactions', icon: 'DollarSign' },
      { id: 'message-management', name: 'Message Management', path: '/admin/messages', category: 'Admin Panel', description: 'Monitor and participate in customer conversations', icon: 'MessageSquare' },
      { id: 'all-urls', name: 'All URLs', path: '/admin/all-urls', category: 'Admin Panel', description: 'View all available pages and URLs', icon: 'Globe' },
      { id: 'merchant-acquisition', name: 'Merchant Acquisition', path: '/admin/merchant-acquisition', category: 'Admin Panel', description: 'Form for registering new suppliers for verification', icon: 'UserPlus' },
      
      // Seller Central
      { id: 'seller-dashboard', name: 'Seller Dashboard', path: '/seller-central', category: 'Seller Central', description: 'Seller performance dashboard', icon: 'BarChart3' },
      { id: 'seller-products', name: 'Seller Products', path: '/seller-central/products', category: 'Seller Central', description: 'Manage product listings and inventory', icon: 'Package' },
      { id: 'seller-orders', name: 'Seller Orders', path: '/seller-central/orders', category: 'Seller Central', description: 'Track and manage customer orders', icon: 'ShoppingCart' },
      { id: 'seller-inventory', name: 'Seller Inventory', path: '/seller-central/inventory', category: 'Seller Central', description: 'Inventory management and stock tracking', icon: 'Package' },
      { id: 'seller-analytics', name: 'Seller Analytics', path: '/seller-central/analytics', category: 'Seller Central', description: 'Performance analytics and reports', icon: 'BarChart3' },
      { id: 'seller-profile', name: 'Seller Profile', path: '/seller-central/profile', category: 'Seller Central', description: 'Company profile and settings', icon: 'User' },
      { id: 'seller-documents', name: 'Document Preparation', path: '/seller-central/documents/prepare', category: 'Seller Central', description: 'Document preparation for orders', icon: 'FileText' },
      
      // Warehouse Management
      { id: 'warehouse-dashboard', name: 'Warehouse Dashboard', path: '/warehouse', category: 'Warehouse Management', description: 'Warehouse operations dashboard', icon: 'BarChart3' },
      { id: 'inventory-management', name: 'Inventory Management', path: '/warehouse/inventory', category: 'Warehouse Management', description: 'Track and manage warehouse inventory', icon: 'Package' },
      { id: 'shipping-management', name: 'Shipping Management', path: '/warehouse/shipping', category: 'Warehouse Management', description: 'Manage outbound shipments and deliveries', icon: 'Truck' },
      { id: 'receiving-management', name: 'Receiving Management', path: '/warehouse/receiving', category: 'Warehouse Management', description: 'Manage incoming shipments and stock', icon: 'Package' },
      { id: 'warehouse-reports', name: 'Warehouse Reports', path: '/warehouse/reports', category: 'Warehouse Management', description: 'Generate warehouse analytics reports', icon: 'FileText' },
      
      // Buyer Pages
      { id: 'buyer-profile', name: 'Buyer Profile', path: '/profile', category: 'Buyer Pages', description: 'Buyer profile management page', icon: 'User' },
      { id: 'buyer-documents', name: 'Buyer Documents', path: '/my-documents', category: 'Buyer Pages', description: 'Documents shared with buyers', icon: 'FileText' },
      { id: 'buyer-orders', name: 'Buyer Orders', path: '/orders', category: 'Buyer Pages', description: 'Order history and tracking', icon: 'ShoppingCart' },
      { id: 'buyer-wishlist', name: 'Buyer Wishlist', path: '/wishlist', category: 'Buyer Pages', description: 'Saved products for later', icon: 'Heart' },
      { id: 'buyer-messages', name: 'Buyer Messages', path: '/messages', category: 'Buyer Pages', description: 'Communication with sellers', icon: 'MessageSquare' }
    ];
  };

  // Group URLs by category
  const groupedUrls = urls.reduce((acc, url) => {
    const category = url.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(url);
    return acc;
  }, {} as Record<string, AdminUrl[]>);

  // Filter URLs based on search and category
  const filteredCategories = Object.entries(groupedUrls)
    .filter(([category]) => filterCategory === 'all' || category.toLowerCase().replace(/\s+/g, '-') === filterCategory)
    .map(([category, urls]) => ({
      category,
      urls: urls.filter(url => {
        const matchesSearch = url.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             (url.description?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
                             url.path.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
      })
    }))
    .filter(category => category.urls.length > 0);

  const categories = [
    { value: 'all', label: 'All Categories' },
    ...Object.keys(groupedUrls).map(category => ({
      value: category.toLowerCase().replace(/\s+/g, '-'),
      label: category
    }))
  ];

  const copyToClipboard = async (url: string) => {
    try {
      const fullUrl = `${window.location.origin}${url}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(''), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Get icon component by name
  const getIconComponent = (iconName: string | null) => {
    if (!iconName) return Globe;
    
    const icons: Record<string, React.FC<{ className?: string }>> = {
      Home, Package, Users, ShoppingCart, Settings, BarChart3,
      User, MessageSquare, FileText, DollarSign, Truck, Star,
      Shield, Building, Store, UserPlus, HelpCircle, Globe, Grid
    };
    
    return icons[iconName] || Globe;
  };

  const totalUrls = urls.length;
  const filteredCount = filteredCategories.reduce((total, category) => total + category.urls.length, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">All URLs</h1>
              <p className="text-gray-600">Complete list of all available pages and routes</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                {filteredCount} of {totalUrls} URLs
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search pages by name, description, or URL..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* URL Categories */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading URLs...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="text-red-500 mb-4">
              <AlertTriangle className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading URLs</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-900">{category.category}</h2>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {category.urls.map((url, urlIndex) => {
                      const IconComponent = getIconComponent(url.icon);
                      return (
                        <div key={urlIndex} className="border border-gray-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-sm transition-all">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <IconComponent className="h-5 w-5 text-orange-600" />
                              </div>
                              <h3 className="font-medium text-gray-900">{url.name}</h3>
                            </div>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => copyToClipboard(url.path)}
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Copy URL"
                              >
                                {copiedUrl === url.path ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </button>
                              <Link
                                to={url.path}
                                target="_blank"
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Open in new tab"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{url.description}</p>
                          <div className="flex items-center space-x-2">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700 font-mono">
                              {url.path}
                            </code>
                            <Link
                              to={url.path}
                              className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                            >
                              Visit
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}

            {filteredCategories.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <Globe className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No URLs found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your search criteria to find more pages.
                </p>
                <button 
                  onClick={() => {
                    setSearchQuery('');
                    setFilterCategory('all');
                  }}
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllUrlsPage;